import {
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Icon,
  IconButton,
  Link,
  Snackbar,
  Stack,
  Tooltip,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";
import axios from "axios.js";
import AppConfig from "appConfig";

export default function LoyaltyPurchasePage() {
  usePageTitle("Purchase Details");

  const { ref } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [purchase, setPurchase] = useState(null);
  const [currency, setCurrency] = useState(null);

  useEffect(() => {
    const fetchPurchase = async () => {
      setLoading(true);

      try {
        const res = await axios.get(`/portal/tm/loyalty/promotions/purchases/${ref}/fetch.json`);

        if (res.data.status === "success") {
          setPurchase(res.data.data.purchase);
          setCurrency(res.data.currency);
        } else {
          setErrMsg(res.data.data.message || "Failed to load purchase.");
          setOpen(true);
        }
      } catch (error) {
        setErrMsg(error.response?.data?.data?.message || "Failed to load purchase.");
        setOpen(true);
      } finally {
        setLoading(false);
      }
    };

    if (ref) fetchPurchase();
  }, [ref]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === "") return "---";

    const amount = Number(value) / 100;

    if (!Number.isFinite(amount)) return value;

    return `${currency}${new Intl.NumberFormat("en-NG", {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)}`;
  };

  const formatDate = (value) => {
    if (!value) return "---";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleString("en-NG", {
      dateStyle: "medium",
      // timeStyle: "short",
    });
  };

  const getMediaUrl = (path) => {
    if (!path) return "";

    // if (/^(https?:\/\/|blob:|data:)/i.test(path)) return path;

    // const normalizedPath = path.startsWith("/") ? path : `/media/${path}`;

    // return normalizedPath;
    return `${AppConfig.api_media_url}${path}`;
  };

  const isPdf = (path) => /\.pdf(?:$|\?)/i.test(path || "");

  const openEvidence = (path) => {
    const url = getMediaUrl(path);

    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const DetailItem = ({ label, value, secondary }) => (
    <Box sx={{ minWidth: 0 }}>
      <MDTypography
        variant="caption"
        color="text"
        fontWeight="medium"
        sx={{
          display: "block",
          opacity: 0.65,
          mb: 0.5,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          fontSize: "0.68rem",
        }}
      >
        {label}
      </MDTypography>

      <MDTypography
        variant="button"
        fontWeight="medium"
        sx={{
          display: "block",
          overflowWrap: "anywhere",
          lineHeight: 1.6,
        }}
      >
        {value === null || value === undefined || value === "" ? "---" : value}
      </MDTypography>

      {secondary && (
        <MDTypography
          variant="caption"
          color="text"
          sx={{ display: "block", opacity: 0.6, mt: 0.25 }}
        >
          {secondary}
        </MDTypography>
      )}
    </Box>
  );

  const SectionTitle = ({ icon, title, subtitle }) => (
    <Stack direction="row" spacing={1.5} alignItems="center" mb={2.5}>
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f0f2f5",
          color: "#344054",
          flexShrink: 0,
        }}
      >
        <Icon fontSize="medium">{icon}</Icon>
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <MDTypography variant="h6" fontWeight="bold">
          {title}
        </MDTypography>

        {subtitle && (
          <MDTypography
            variant="button"
            color="text"
            sx={{ display: "block", opacity: 0.65, mt: 0.25 }}
          >
            {subtitle}
          </MDTypography>
        )}
      </Box>
    </Stack>
  );

  const EvidenceCard = ({ title, subtitle, path, icon }) => {
    const url = getMediaUrl(path);
    const pdf = isPdf(path);

    return (
      <Card
        sx={{
          height: "100%",
          border: "1px solid #e8eaf0",
          borderRadius: "12px",
          boxShadow: "none",
          overflow: "hidden",
          transition: "border-color 0.2s ease",
          "&:hover": {
            borderColor: "#aeb5c2",
          },
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: "1px solid #edf0f4",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "9px",
                bgcolor: "#f0f2f5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Icon sx={{ color: "#475467" }}>{icon}</Icon>
            </Box>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <MDTypography variant="button" fontWeight="bold">
                {title}
              </MDTypography>

              <MDTypography
                variant="caption"
                color="text"
                sx={{ display: "block", opacity: 0.65, mt: 0.25 }}
              >
                {subtitle}
              </MDTypography>
            </Box>

            {path ? (
              <Chip
                label="Uploaded"
                size="small"
                sx={{
                  bgcolor: "#e8f5ed",
                  color: "#247044",
                  fontWeight: 600,
                  fontSize: "0.68rem",
                }}
              />
            ) : (
              <Chip
                label="Missing"
                size="small"
                sx={{
                  bgcolor: "#fff0ed",
                  color: "#b42318",
                  fontWeight: 600,
                  fontSize: "0.68rem",
                }}
              />
            )}
          </Stack>
        </Box>

        <Box
          sx={{
            p: 2,
            bgcolor: "#f8f9fb",
            minHeight: 260,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {!path ? (
            <Stack alignItems="center" spacing={1}>
              <Icon sx={{ fontSize: 42, color: "#c4c9d2" }}>image_not_supported</Icon>

              <MDTypography variant="button" color="text">
                No evidence uploaded
              </MDTypography>
            </Stack>
          ) : pdf ? (
            <Stack alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: 82,
                  height: 96,
                  borderRadius: "8px",
                  bgcolor: "#fff",
                  border: "1px solid #e4e7ec",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                }}
              >
                <Icon sx={{ fontSize: 36, color: "#d92d20" }}>picture_as_pdf</Icon>

                <MDTypography variant="caption" fontWeight="bold" color="text">
                  PDF
                </MDTypography>
              </Box>

              <MDTypography variant="button" color="text" sx={{ textAlign: "center" }}>
                PDF document
              </MDTypography>
            </Stack>
          ) : (
            <Box
              component="img"
              src={url}
              alt={title}
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
              sx={{
                display: "block",
                maxWidth: "100%",
                width: "100%",
                maxHeight: 360,
                objectFit: "contain",
                borderRadius: "6px",
                cursor: "zoom-in",
              }}
              onClick={() => openEvidence(path)}
            />
          )}
        </Box>

        <Divider />

        <Box
          sx={{
            px: 2.5,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <MDTypography variant="caption" color="text">
            {path ? (pdf ? "PDF document" : "Image evidence") : "Not provided"}
          </MDTypography>

          {path && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Icon>open_in_new</Icon>}
              onClick={() => openEvidence(path)}
              sx={{
                textTransform: "none",
                borderColor: "#d0d5dd",
                color: "#344054",
                whiteSpace: "nowrap",
              }}
            >
              View original
            </Button>
          )}
        </Box>
      </Card>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setOpen(false)} severity="error" sx={{ width: "100%" }}>
          {errMsg}
        </Alert>
      </Snackbar>

      <MDBox mt={2} mb={4}>
        <Button
          variant="outlined"
          startIcon={<Icon>arrow_back</Icon>}
          onClick={() => navigate(-1)}
          sx={{
            mb: 2,
            textTransform: "none",
            borderColor: "#d0d5dd",
            color: "#344054",
            borderRadius: "8px",
            "&:hover": {
              borderColor: "#98a2b3",
              backgroundColor: "#f8f9fb",
            },
          }}
        >
          Back to purchases
        </Button>
        {!loading && !purchase && (
          <Card sx={{ p: 4, textAlign: "center", borderRadius: "12px" }}>
            <Icon sx={{ fontSize: 44, color: "#98a2b3", mb: 1 }}>receipt_long</Icon>

            <MDTypography variant="h6" fontWeight="bold">
              Purchase unavailable
            </MDTypography>

            <MDTypography variant="button" color="text">
              Purchase details could not be displayed.
            </MDTypography>
          </Card>
        )}

        {purchase && (
          <Stack spacing={2.5}>
            {/* Purchase header */}
            <Card
              sx={{
                borderRadius: "12px",
                boxShadow: "none",
                border: "1px solid #e8eaf0",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  p: { xs: 2.5, md: 3.5 },
                  background: "linear-gradient(120deg, #202936 0%, #344054 100%)",
                  color: "#fff",
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Icon sx={{ color: "rgba(255,255,255,0.8)" }}>receipt_long</Icon>

                      <MDTypography
                        variant="button"
                        sx={{
                          color: "rgba(255,255,255,0.75)",
                          letterSpacing: 1,
                          fontWeight: "medium",
                        }}
                      >
                        LOYALTY PURCHASE
                      </MDTypography>
                    </Stack>

                    <MDTypography
                      variant="h4"
                      fontWeight="bold"
                      sx={{
                        color: "#fff",
                        overflowWrap: "anywhere",
                        fontSize: { xs: "1.4rem", md: "2rem" },
                      }}
                    >
                      {purchase.purchase_reference || purchase.reference}
                    </MDTypography>

                    <MDTypography
                      variant="button"
                      sx={{
                        display: "block",
                        mt: 1,
                        color: "rgba(255,255,255,0.7)",
                      }}
                    >
                      Submitted {formatDate(purchase.created)}
                    </MDTypography>
                  </Box>

                  <Chip
                    icon={
                      <Icon
                        sx={{
                          color: purchase.verified ? "#067647 !important" : "#b54708 !important",
                          fontSize: "18px !important",
                        }}
                      >
                        {purchase.verified ? "verified" : "schedule"}
                      </Icon>
                    }
                    label={purchase.verified ? "Verified" : "Pending verification"}
                    sx={{
                      bgcolor: purchase.verified ? "#d1fadf" : "#fef0c7",
                      color: purchase.verified ? "#067647" : "#b54708",
                      fontWeight: 700,
                      borderRadius: "8px",
                      height: 36,
                    }}
                  />
                </Stack>
              </Box>

              <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box
                      sx={{
                        p: 2,
                        height: "100%",
                        border: "1px solid #eaecf0",
                        borderRadius: "10px",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Icon sx={{ color: "#667085" }}>stars</Icon>

                        <MDTypography variant="caption" color="text">
                          POINTS AWARDED
                        </MDTypography>
                      </Stack>

                      <MDTypography variant="h4" fontWeight="bold" sx={{ mt: 1, color: "#202936" }}>
                        {purchase.points}
                      </MDTypography>

                      <MDTypography variant="caption" color="text">
                        Loyalty points
                      </MDTypography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Box
                      sx={{
                        p: 2,
                        height: "100%",
                        border: "1px solid #eaecf0",
                        borderRadius: "10px",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Icon sx={{ color: "#667085" }}>sell</Icon>

                        <MDTypography variant="caption" color="text">
                          UNIT PRICE
                        </MDTypography>
                      </Stack>

                      <MDTypography
                        variant="h4"
                        fontWeight="bold"
                        sx={{
                          mt: 1,
                          color: "#202936",
                          fontSize: { xs: "1.5rem", md: "1.7rem" },
                          overflowWrap: "anywhere",
                        }}
                      >
                        {formatCurrency(purchase.purchase_unit_price)}
                      </MDTypography>

                      <MDTypography variant="caption" color="text">
                        Price per unit
                      </MDTypography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Box
                      sx={{
                        p: 2,
                        height: "100%",
                        border: "1px solid #eaecf0",
                        borderRadius: "10px",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Icon sx={{ color: "#667085" }}>shopping_bag</Icon>

                        <MDTypography variant="caption" color="text">
                          QUANTITY
                        </MDTypography>
                      </Stack>

                      <MDTypography variant="h4" fontWeight="bold" sx={{ mt: 1, color: "#202936" }}>
                        {purchase.purchase_quantity ?? "---"}
                      </MDTypography>

                      <MDTypography variant="caption" color="text">
                        Units purchased
                      </MDTypography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Box
                      sx={{
                        p: 2,
                        height: "100%",
                        border: "1px solid #eaecf0",
                        borderRadius: "10px",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Icon sx={{ color: "#667085" }}>payments</Icon>

                        <MDTypography variant="caption" color="text">
                          PURCHASE TOTAL
                        </MDTypography>
                      </Stack>

                      <MDTypography
                        variant="h4"
                        fontWeight="bold"
                        sx={{
                          mt: 1,
                          color: "#202936",
                          fontSize: { xs: "1.5rem", md: "1.7rem" },
                          overflowWrap: "anywhere",
                        }}
                      >
                        {formatCurrency(purchase.purchase_total_price)}
                      </MDTypography>

                      <MDTypography variant="caption" color="text">
                        Purchase amount
                      </MDTypography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Card>

            {/* Consumer */}
            <Card
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: "12px",
                boxShadow: "none",
                border: "1px solid #e8eaf0",
              }}
            >
              <SectionTitle
                icon="person_outline"
                title="Consumer details"
                subtitle="Information belonging to the registered participant."
              />

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <DetailItem
                    label="Full name"
                    value={
                      `${purchase.consumer?.first_name || ""} ${
                        purchase.consumer?.last_name || ""
                      }`.trim() || "---"
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <DetailItem label="Email address" value={purchase.consumer?.email_address} />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <DetailItem label="Phone number" value={purchase.consumer?.phone_number} />
                </Grid>

                {/* <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12}>
                  <DetailItem label="Consumer reference" value={purchase.consumer?.reference} />
                </Grid> */}
              </Grid>
            </Card>

            {/* Evidence */}
            <Card
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: "12px",
                boxShadow: "none",
                border: "1px solid #e8eaf0",
              }}
            >
              <SectionTitle
                icon="perm_media"
                title="Purchase evidence"
                subtitle="Review the uploaded product photo and purchase receipt."
              />

              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <EvidenceCard
                    title="Product photo"
                    subtitle="Photo of the purchased product"
                    path={purchase.product_image}
                    icon="inventory_2"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <EvidenceCard
                    title="Purchase receipt"
                    subtitle="Receipt or other purchase evidence"
                    path={purchase.purchase_evidence}
                    icon="receipt_long"
                  />
                </Grid>
              </Grid>
            </Card>

            {/* Product and purchase */}
            <Card
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: "12px",
                boxShadow: "none",
                border: "1px solid #e8eaf0",
              }}
            >
              <SectionTitle
                icon="shopping_bag"
                title="Product and purchase details"
                subtitle="Product selected and transaction information."
              />

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <DetailItem label="Product" value={purchase.product?.title} />
                </Grid>

                {/* <Grid item xs={12} sm={6} md={4}>
                  <DetailItem label="Product reference" value={purchase.product?.reference} />
                </Grid> */}

                <Grid item xs={12} sm={6} md={4}>
                  <DetailItem
                    label="Product listed price"
                    value={formatCurrency(purchase.product?.price * 100)}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <DetailItem
                    label="Product reward points"
                    value={purchase.product?.reward_points}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <DetailItem label="Merchant" value={purchase.purchase_merchant} />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <DetailItem label="Receipt reference" value={purchase.purchase_reference} />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <DetailItem label="Purchase date" value={formatDate(purchase.purchase_date)} />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <DetailItem label="Purchase quantity" value={purchase.purchase_quantity} />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <DetailItem
                    label="Purchase unit price"
                    value={formatCurrency(purchase.purchase_unit_price)}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <DetailItem
                    label="Purchase total"
                    value={formatCurrency(purchase.purchase_total_price)}
                  />
                </Grid>
              </Grid>
            </Card>

            {/* Territory and verification */}
            <Card
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: "12px",
                boxShadow: "none",
                border: "1px solid #e8eaf0",
              }}
            >
              <SectionTitle
                icon="fact_check"
                title="Verification and territory"
                subtitle="Current verification status and purchase location."
              />

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <Box>
                    <MDTypography
                      variant="caption"
                      color="text"
                      sx={{
                        display: "block",
                        opacity: 0.65,
                        mb: 1,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      Verification status
                    </MDTypography>

                    <Chip
                      icon={
                        <Icon
                          sx={{
                            color: purchase.verified ? "#067647 !important" : "#b54708 !important",
                          }}
                        >
                          {purchase.verified ? "check_circle" : "pending"}
                        </Icon>
                      }
                      label={purchase.verified ? "Verified" : "Not verified"}
                      sx={{
                        bgcolor: purchase.verified ? "#d1fadf" : "#fef0c7",
                        color: purchase.verified ? "#067647" : "#b54708",
                        fontWeight: 700,
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <DetailItem
                    label="Territory"
                    value={purchase.territory?.title || "Not assigned"}
                  />
                </Grid>

                {/* <Grid item xs={12} sm={6} md={4}>
                  <DetailItem label="Territory reference" value={purchase.territory?.reference} />
                </Grid> */}

                <Grid item xs={12} sm={6} md={4}>
                  <DetailItem label="Purchase record reference" value={purchase.reference} />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <DetailItem label="Record created" value={formatDate(purchase.created)} />
                </Grid>
              </Grid>
            </Card>
          </Stack>
        )}
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}
